import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, LinearProgress, Chip, List, ListItem, ListItemText,
    CircularProgress, Stack, ThemeProvider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import api from '../services/api';
import lightTheme from '../themes/lightTheme';

/**
 * Polla GET /tasks/{taskId} a cada 5s e mostra progresso.
 *
 * Props:
 *   open       boolean
 *   taskId     int|null
 *   title      string opcional
 *   onClose    () => void
 *   onComplete (task) => void   chamado quando status=done; ideal pra refresh
 */
const POLL_INTERVAL_MS = 5000;
const STATUS_LABEL = {
    queued:  { label: 'Na fila',         color: 'default', icon: <HourglassBottomIcon fontSize="small" /> },
    running: { label: 'Em execução',     color: 'info',    icon: <CircularProgress size={14} /> },
    done:    { label: 'Concluído',       color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    failed:  { label: 'Falhou',          color: 'error',   icon: <ErrorIcon fontSize="small" /> },
};

export default function TaskProgress({ open, taskId, title = "Sincronização DNS", onClose, onComplete }) {
    const [task, setTask] = useState(null);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);
    const completedRef = useRef(false);

    useEffect(() => {
        if (!open || !taskId) {
            setTask(null);
            setError(null);
            completedRef.current = false;
            return;
        }

        let cancelled = false;

        const tick = async () => {
            try {
                const res = await api.get(`/tasks/${taskId}`);
                if (cancelled) return;
                const t = res.data?.data;
                setTask(t);

                if (t && (t.status === 'done' || t.status === 'failed') && !completedRef.current) {
                    completedRef.current = true;
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    if (t.status === 'done' && onComplete) onComplete(t);
                }
            } catch (e) {
                if (cancelled) return;
                setError(e.response?.data?.detail || e.message || 'Erro ao consultar task');
            }
        };

        // Primeira leitura imediata
        tick();
        // Polling
        intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [open, taskId]);

    const statusInfo = task ? (STATUS_LABEL[task.status] || { label: task.status, color: 'default', icon: null }) : null;
    const progress = task?.progress || {};
    const perNode = progress.per_node || {};
    const phase = progress.phase || 'pending';
    const isDone = task?.status === 'done';
    const isFailed = task?.status === 'failed';
    const isRunning = task?.status === 'running' || task?.status === 'queued';

    return (
        <ThemeProvider theme={lightTheme}>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {!task && !error && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" sx={{ ml: 2 }}>Aguardando task...</Typography>
                    </Box>
                )}

                {error && (
                    <Typography color="error" variant="body2">{error}</Typography>
                )}

                {task && (
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                                size="small"
                                icon={statusInfo.icon}
                                label={statusInfo.label}
                                color={statusInfo.color}
                            />
                            {progress.domain_count != null && (
                                <Chip size="small" variant="outlined" label={`${progress.domain_count} domínios`} />
                            )}
                            {progress.range_count != null && (
                                <Chip size="small" variant="outlined" label={`${progress.range_count} ranges`} />
                            )}
                            {progress.zone_count != null && (
                                <Chip size="small" variant="outlined" label={`${progress.zone_count} zonas`} />
                            )}
                        </Stack>

                        {isRunning && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress />
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                    {phase === 'fetching_domains' && 'Carregando lista de domínios…'}
                                    {phase === 'syncing_nodes' && 'Sincronizando nós DNS via SSH…'}
                                    {phase === 'pending' && 'Aguardando worker pegar a task…'}
                                    {' '}— atualiza a cada {POLL_INTERVAL_MS / 1000}s
                                </Typography>
                            </Box>
                        )}

                        {/* Resultado por nó (quando termina ou em progresso) */}
                        {Object.keys(perNode).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Status dos nós DNS
                                </Typography>
                                <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                                    {Object.entries(perNode).map(([nodeId, info]) => {
                                        const ok = info.status === 'ok';
                                        return (
                                            <ListItem key={nodeId} sx={{ py: 0.5 }}>
                                                {ok
                                                    ? <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} />
                                                    : <ErrorIcon color="error" sx={{ mr: 1, fontSize: 18 }} />
                                                }
                                                <ListItemText
                                                    primary={info.name}
                                                    secondary={ok ? 'Sincronizado' : (info.message || 'Falha ao sincronizar')}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {isFailed && task.error && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#7a1a1a', fontFamily: 'monospace' }}>
                                    {task.error}
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                            Task #{task.id} · criada {new Date(task.created_at).toLocaleString('pt-BR')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {isDone || isFailed ? 'Fechar' : 'Fechar (continua em background)'}
                </Button>
            </DialogActions>
        </Dialog>
        </ThemeProvider>
    );
}
